import { useRef, useEffect } from "react";

/**
 * Lightweight canvas "3D" moment: a slowly rotating wireframe point-sphere.
 * Pure canvas 2D (no heavy deps) for reliability. Subtle, dark, monochrome.
 */
export default function HeroCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h, dpr;

    // build sphere points
    const N = 460;
    const R = 1;
    const pts = [];
    for (let i = 0; i < N; i++) {
      const phi = Math.acos(1 - (2 * (i + 0.5)) / N);
      const theta = Math.PI * (1 + Math.sqrt(5)) * i;
      pts.push([
        R * Math.sin(phi) * Math.cos(theta),
        R * Math.sin(phi) * Math.sin(theta),
        R * Math.cos(phi),
      ]);
    }

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let angle = 0;
    const render = () => {
      ctx.clearRect(0, 0, w, h);
      angle += 0.0018;
      const cx = w / 2;
      const cy = h / 2;
      const radius = Math.min(w, h) * 0.42;
      const cosA = Math.cos(angle);
      const sinA = Math.cos(angle * 0.7);
      const rotY = Math.sin(angle);

      for (const p of pts) {
        // rotate around Y then X
        let x = p[0] * Math.cos(angle) - p[2] * Math.sin(angle);
        let z = p[0] * Math.sin(angle) + p[2] * Math.cos(angle);
        let y = p[1] * cosA - z * sinA * 0.25;
        z = p[1] * sinA * 0.25 + z * cosA;

        const perspective = 2.4 / (2.4 + z);
        const sx = cx + x * radius * perspective;
        const sy = cy + y * radius * perspective;
        const depth = (z + 1) / 2; // 0..1
        const size = 0.6 + perspective * 1.4;

        ctx.beginPath();
        ctx.arc(sx, sy, size, 0, Math.PI * 2);
        // most points white with low alpha, a few accent red
        ctx.fillStyle = `rgba(255,255,255,${0.12 + depth * 0.5})`;
        ctx.fill();
      }
      // subtle accent ring dot
      ctx.beginPath();
      ctx.arc(cx + radius * 0.8 * Math.cos(angle * 2), cy + radius * 0.3 * rotY, 2.4, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,59,48,0.9)";
      ctx.fill();

      raf = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="w-full h-full block" aria-hidden="true" />;
}
